import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { Modal, Button, Group, Stack, Text, Checkbox, Alert, Loader, Divider } from "@mantine/core";
import {
    INITIATE_POSITION_OPEN,
    ABANDON_POSITION_OPEN,
    COMPLETE_POSITION_OPEN,
} from "../../queries/waves";

/* ============================================================================
 * Wave O — Obstruction Arm C: symmetric ENTRY flow (endpointsforfrontend.md
 * §5j–5n, implementation.md CHANGE 5; report/NUSwap_Final_Implementation_Plan.md
 * §17/§17.5).
 *
 * Rendered for Phase 3 Arm C participants opening a position (Buy or Short).
 * Mirrors the 4-step exit flow so opening a position takes the same
 * deliberate effort as closing one — the manipulative easy-entry / hard-exit
 * asymmetry is removed.
 *   open → initiatePositionOpen (server validates arm = C, returns attempt_id)
 *        → 4-step UI (review → risk → confirm → 10s timer)
 *        → completePositionOpen → onComplete()  (parent then submits the order)
 *        or abandonPositionOpen → onAbandon()   (parent does nothing)
 *   120s with no completion → auto-abandon.
 *
 * The backend has no server-side 120s timer — this component enforces it.
 * ==========================================================================*/

const OPEN_TIMEOUT_MS = 120_000;
const FORCED_REVIEW_SECONDS = 10;

const money = (n) => {
    const v = Number(n) || 0;
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ObstructionOpenModal({
    opened,
    positionSymbol,   // e.g. "BTCUSD" — the asset the position is being opened on
    quantity,         // intended entry quantity (number)
    price,            // latest/estimated unit price for the summary (number | null)
    onComplete,       // () => void  — proceed to actually submit the order
    onAbandon,        // () => void  — close modal without opening the position
    onFallbackClose,  // () => void  — no active session → submit a normal order
}) {
    // "initiating" | "flow" | "error"
    const [stage, setStage] = useState("initiating");
    const [attemptId, setAttemptId] = useState(null);
    const [step, setStep] = useState(1);          // 1..4
    const [riskAck, setRiskAck] = useState(false);
    const [countdown, setCountdown] = useState(FORCED_REVIEW_SECONDS);
    const [errorMsg, setErrorMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    const timerRef = useRef(null);
    const finalisedRef = useRef(false); // guards against double abandon/complete

    // Live mirror so the 120s auto-abandon closure reports the current step.
    const stepRef = useRef(1);
    stepRef.current = step;

    const [initiate] = useMutation(INITIATE_POSITION_OPEN);
    const [abandon] = useMutation(ABANDON_POSITION_OPEN);
    const [complete] = useMutation(COMPLETE_POSITION_OPEN);

    const estCost = price != null ? Number(price) * (Number(quantity) || 0) : null;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    // --- finalisers -------------------------------------------------------
    const doAbandon = useCallback(async () => {
        if (finalisedRef.current) { onAbandon(); return; }
        finalisedRef.current = true;
        clearTimer();
        if (attemptId) {
            try {
                await abandon({ variables: { attemptId, openFlowStepReached: stepRef.current } });
            } catch (_) { /* already finalised server-side — ignore */ }
        }
        onAbandon();
    }, [attemptId, abandon, onAbandon]);

    const doComplete = useCallback(async () => {
        if (finalisedRef.current) return;
        finalisedRef.current = true;
        clearTimer();
        setBusy(true);
        try {
            // No realised P&L on entry — pass null. The order submission follows.
            await complete({ variables: { attemptId, pnlAtOpen: null } });
        } catch (_) { /* already finalised — proceed to submit anyway */ }
        setBusy(false);
        onComplete();
    }, [attemptId, complete, onComplete]);

    // --- kick off the attempt when the modal opens ------------------------
    useEffect(() => {
        if (!opened) return;
        let cancelled = false;

        finalisedRef.current = false;
        setStage("initiating");
        setStep(1);
        setRiskAck(false);
        setCountdown(FORCED_REVIEW_SECONDS);
        setErrorMsg(null);

        (async () => {
            try {
                const { data } = await initiate({
                    variables: { positionSymbol: String(positionSymbol), quantity: Number(quantity) },
                });
                if (cancelled) return;
                setAttemptId(data.initiatePositionOpen.attempt_id);
                setStage("flow");

                clearTimer();
                timerRef.current = setTimeout(() => { doAbandon(); }, OPEN_TIMEOUT_MS);
            } catch (err) {
                if (cancelled) return;
                // Between session windows in Phase 3 — fall back to a normal order
                // rather than blocking the participant (§5k error table).
                if (err.message.includes("No active session")) {
                    onFallbackClose();
                    return;
                }
                setErrorMsg(err.message);
                setStage("error");
            }
        })();

        return () => { cancelled = true; clearTimer(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, positionSymbol, quantity]);

    // --- step 4 forced-review countdown -----------------------------------
    useEffect(() => {
        if (stage !== "flow" || step !== 4) return;
        setCountdown(FORCED_REVIEW_SECONDS);
        const iv = setInterval(() => {
            setCountdown((c) => {
                if (c <= 1) { clearInterval(iv); return 0; }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(iv);
    }, [stage, step]);

    const qtyLabel = `${quantity} ${positionSymbol}`;

    // Prevent closing via backdrop/esc from silently skipping the abandon log.
    const handleModalClose = () => { doAbandon(); };

    return (
        <Modal
            opened={opened}
            onClose={handleModalClose}
            title="Open Position"
            centered
            closeOnClickOutside={false}
            withCloseButton={false}
        >
            {stage === "initiating" && (
                <Group position="center" py="lg">
                    <Loader size="sm" /><Text>Preparing your order…</Text>
                </Group>
            )}

            {stage === "error" && (
                <Stack>
                    <Alert color="red" title="Unable to open right now">{errorMsg}</Alert>
                    <Group position="right">
                        <Button variant="default" onClick={onAbandon}>Dismiss</Button>
                    </Group>
                </Stack>
            )}

            {stage === "flow" && (
                <Stack>
                    <Text size="sm" color="dimmed">Step {step} of 4</Text>

                    {step === 1 && (
                        <>
                            <Text weight={600}>Review Your Order</Text>
                            <Divider />
                            <Row label="Asset" value={positionSymbol} />
                            <Row label="Quantity" value={qtyLabel} />
                            {price != null && <Row label="Est. price" value={money(price)} />}
                            {estCost != null && <Row label="Est. cost" value={money(estCost)} />}
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button onClick={() => setStep(2)}>Continue →</Button>
                            </Group>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <Text weight={600}>Market Risk</Text>
                            <Divider />
                            <Checkbox
                                checked={riskAck}
                                onChange={(e) => setRiskAck(e.currentTarget.checked)}
                                label="I understand that opening this position carries market risk and its value can fall."
                            />
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button disabled={!riskAck} onClick={() => setStep(3)}>Next →</Button>
                            </Group>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <Text weight={600}>Confirm Order</Text>
                            <Divider />
                            <Row label="Asset" value={positionSymbol} />
                            <Row label="Quantity" value={qtyLabel} />
                            {price != null && <Row label="Est. price" value={money(price)} />}
                            {estCost != null && <Row label="Est. cost" value={money(estCost)} />}
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button onClick={() => setStep(4)}>Confirm Order →</Button>
                            </Group>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            <Text weight={600}>Final Review</Text>
                            <Divider />
                            <Text size="sm">Your order is ready. Please review for {FORCED_REVIEW_SECONDS} seconds.</Text>
                            <Text align="center" size="lg" weight={700} my="sm">
                                {countdown > 0 ? `⏱ ${countdown} second${countdown === 1 ? "" : "s"} remaining…` : "Ready"}
                            </Text>
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon} disabled={busy}>Cancel</Button>
                                <Button color="blue" disabled={countdown > 0 || busy} loading={busy} onClick={doComplete}>
                                    Submit
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            )}
        </Modal>
    );
}

function Row({ label, value }) {
    return (
        <Group position="apart">
            <Text color="dimmed" size="sm">{label}</Text>
            <Text weight={500}>{value}</Text>
        </Group>
    );
}
